export const INVOICE_STATUS = {
  PAID: 'PAID',
  VOIDED: 'VOIDED',
  PAST_DUE: 'PAST_DUE',
}

export const getInvoiceStatus = (invoice) => invoice?.status?.toUpperCase() ?? null

export const getInvoicePageTitle = (invoice) => {
  const recipientName = invoice?.contact?.full_name?.trim()
  const invoiceNumber = invoice?.invoice_number?.trim?.()
    ?? (invoice?.invoice_number != null ? String(invoice.invoice_number).trim() : '')

  if (!recipientName || !invoiceNumber) {
    return 'Calendly'
  }

  return `${recipientName} Invoice #${invoiceNumber} - Calendly`
}

const toSafeNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const computeLineItemAmountCents = (lineItem = {}) =>
  toSafeNumber(lineItem.quantity) * toSafeNumber(lineItem.unit_price_cents)

const computeSubtotalCents = (lineItems = []) =>
  lineItems.reduce((sum, item) => sum + computeLineItemAmountCents(item), 0)

const computeDiscountCents = (discount = {}, subtotalCents = 0) => {
  if (discount.type === 'PERCENT') {
    return Math.round(subtotalCents * (toSafeNumber(discount.percent) / 100))
  }

  return toSafeNumber(discount.amount_cents)
}

const computeDiscountTotalCents = (
  discountLineItems = [],
  subtotalCents = 0
) =>
  discountLineItems.reduce(
    (sum, discount) => sum + computeDiscountCents(discount, subtotalCents),
    0
  )

export const discountRowLabel = (discountLineItems = [], fallbackLabel) => {
  if (!Array.isArray(discountLineItems) || discountLineItems.length === 0) {
    return fallbackLabel
  }

  const trimmedNames = discountLineItems
    .map((d) => (typeof d?.name === 'string' ? d.name.trim() : ''))
    .filter(Boolean)

  if (trimmedNames.length === 0) {
    return fallbackLabel
  }

  const uniqueNames = [...new Set(trimmedNames)]

  if (trimmedNames.length === discountLineItems.length) {
    return uniqueNames.join(', ')
  }

  return `${uniqueNames.join(', ')}, ${fallbackLabel}`
}

const parseInvoiceTaxRatePercent = (taxRate) => {
  if (taxRate === null || taxRate === undefined) {
    return null
  }

  const trimmed = `${taxRate}`.trim()
  if (trimmed === '') {
    return null
  }

  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

export const computeInvoiceTotals = (invoice = {}) => {
  const lineItems = invoice.line_items || []
  const discounts = invoice.discount_line_items || []
  const subtotalCents = computeSubtotalCents(lineItems)
  const discountTotalCents = computeDiscountTotalCents(discounts, subtotalCents)
  const taxableCents = Math.max(0, subtotalCents - discountTotalCents)
  const parsedTaxRate = parseInvoiceTaxRatePercent(invoice?.invoice_settings?.tax_rate) ?? 0
  const taxTotalCents = Math.round(taxableCents * (parsedTaxRate / 100))
  const totalCents = taxableCents + taxTotalCents

  return {
    subtotalCents,
    discountTotalCents,
    taxableCents,
    taxTotalCents,
    totalCents,
    parsedTaxRate,
  }
}

const INVOICE_PAYMENT_FRESHNESS_ERROR_KEYS = {
  paid: 'booking.invoice.errors.stale_invoice_status',
  voided: 'booking.invoice.errors.stale_invoice_voided',
  price_changed: 'booking.invoice.errors.stale_invoice_price_changed',
}

/**
 * Returns an i18n error key when the freshly fetched invoice is no longer
 * payable at the amount shown on the current page, or null when payment may proceed.
 */
export const getInvoicePaymentFreshnessErrorKey = (currentInvoice, freshInvoice) => {
  const status = getInvoiceStatus(freshInvoice)

  if (status === INVOICE_STATUS.PAID) {
    return INVOICE_PAYMENT_FRESHNESS_ERROR_KEYS.paid
  }

  if (status === INVOICE_STATUS.VOIDED) {
    return INVOICE_PAYMENT_FRESHNESS_ERROR_KEYS.voided
  }

  const currentTotalCents = computeInvoiceTotals(currentInvoice).totalCents
  const freshTotalCents = computeInvoiceTotals(freshInvoice).totalCents

  if (currentTotalCents !== freshTotalCents) {
    return INVOICE_PAYMENT_FRESHNESS_ERROR_KEYS.price_changed
  }

  return null
}
